import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../utils/api';
import { lockBodyScroll } from '../utils/scrollLock';
import { ImageViewer } from './PuzzleImageViewer';
import { X, Loader, AlertTriangle, Eye } from 'lucide-react';

// 前台 · 证券“补全识别失败的信息”（从工作台“图片管理”里点“去补全”进来）
//   识别成功的记录不会出现这个入口。只有提交后仍留空的 微信号 / 昵称 / 操作微信号 可以补（rec.user_fix_fields），
//   电话不能改；每项只能补一次，补完服务端自动重新合成最终图。再错请联系管理员 / 代理商，后台“更正字段”可以改任何字段。
//   最终图没生成出来时（rec.user_fix_regen），可以不填内容、直接“重新生成最终图”。
// onDone(record, composeOk)：保存成功；onClose(needRefresh)：关闭（needRefresh=服务端说这条记录状态变了，需要刷新列表）
const LABEL = { wechat_id: '微信号', nickname: '昵称', operator_wx: '操作微信号' };
const HINT = {
  wechat_id: '对照客户资料图填写',
  nickname: '对照客户资料图填写',
  operator_wx: '对照主图填写',
};

export default function PuzzleFixModal({ rec, onClose, onDone }) {
  const fields = rec.user_fix_fields || [];
  const regenOnly = fields.length === 0 && !!rec.user_fix_regen;
  const [vals, setVals] = useState(() => Object.fromEntries(fields.map(f => [f, ''])));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [viewing, setViewing] = useState(false);

  useEffect(() => lockBodyScroll(), []);

  // 只给看三张原图（对照填写用），不放最终图 / 字段图
  const originals = { images: { chat: rec.images?.chat, profile: rec.images?.profile, main: rec.images?.main } };
  const hasOriginals = !!(originals.images.chat || originals.images.profile || originals.images.main);
  const filled = fields.filter(f => String(vals[f] || '').trim());
  const canSave = !saving && (regenOnly || filled.length > 0);

  const save = async () => {
    if (!canSave) return;
    setSaving(true); setErr('');
    try {
      const body = { recordId: rec.id, fields: Object.fromEntries(filled.map(f => [f, String(vals[f]).trim()])) };
      const res = await api.post('/puzzle/record/fix', body, { timeout: 60000 });
      const d = res.data || {};
      if (d.status === 'OK') { onDone && onDone(d.record, !!d.compose_ok); return; }
      setErr(d.message || (d.status === 'NOT_FOUND' ? '记录不存在' : '保存失败，请重试'));
      // 服务端说这条记录已经变了（已被补过 / 已导出 / 已作废…）：提示后刷新列表，避免用户对着过期数据反复点
      if (['ALREADY_OK', 'CLOSED', 'NOT_FOUND'].includes(d.status)) setTimeout(() => onClose && onClose(true), 1600);
    } catch (e) {
      setErr(e.response?.data?.error || '保存失败，请重试');
    } finally { setSaving(false); }
  };

  const close = () => { if (!saving) onClose && onClose(false); };

  // 原图查看器放在遮罩外面（同级）：React 事件会穿过 portal 冒泡到父组件，放在遮罩里面的话，点一下关闭查看器会把补全弹窗一起关掉
  return (<>
    {createPortal(
    <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 9997, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" style={{
        background: 'var(--c-surface)', borderRadius: '22px 22px 0 0', width: '100%', maxWidth: 480, maxHeight: '93vh',
        display: 'flex', flexDirection: 'column', paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 18px 10px' }}>
          <AlertTriangle size={18} color="var(--c-accent)" />
          <span style={{ fontSize: 17, fontWeight: 700, flex: 1 }}>{regenOnly ? '重新生成最终图' : '补全识别失败的信息'}</span>
          <button onClick={close} disabled={saving} style={{ background: 'var(--c-bg)', border: 'none', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={16} color="var(--c-text-secondary)" />
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '4px 18px 20px' }}>
          <div style={{ background: 'var(--c-bg)', borderRadius: 12, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: 0.5 }}>{rec.phone || '—'}</div>
              <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {rec.package_name}{rec.seq_no != null ? ` · 第 ${rec.seq_no} 单` : ''}
              </div>
            </div>
            <span style={{ fontSize: 11, color: 'var(--c-text-tertiary)', flexShrink: 0 }}>电话不可修改</span>
          </div>

          {regenOnly ? (
            <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', lineHeight: 1.7, marginTop: 12 }}>
              这条记录的最终图片还没有生成成功。点下面的按钮重新生成；如果多次仍失败，请联系管理员在后台“重新合成”。
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', lineHeight: 1.7, marginTop: 12 }}>
                下面这些信息没有从图片里识别出来。请对照原图补全，补全后会自动重新生成最终图。
                <strong style={{ color: 'var(--c-danger)' }}>每项只能补一次</strong>，补错请联系管理员或代理商更正。
              </div>
              {hasOriginals && (
                <button onClick={() => setViewing(true)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, margin: '8px 0 2px', padding: 0, background: 'none', border: 'none',
                  color: 'var(--c-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}><Eye size={14} /> 查看原图（对照填写）</button>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                {fields.map(f => (
                  <div key={f}>
                    <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 4 }}>
                      {LABEL[f]}<span style={{ color: 'var(--c-text-tertiary)', marginLeft: 6 }}>{HINT[f]}</span>
                    </div>
                    <input className="input" style={{ fontSize: 16, padding: '10px 12px' }} value={vals[f]} maxLength={60} disabled={saving}
                      onChange={e => setVals(s => ({ ...s, [f]: e.target.value }))} placeholder={`请输入${LABEL[f]}，没有可留空`} />
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginTop: 8, lineHeight: 1.6 }}>
                留空的项以后仍可补；填了的项保存后就不能再改。
              </div>
            </>
          )}

          {err && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--c-danger)', lineHeight: 1.5 }}>{err}</div>}

          <button className="btn btn-primary" onClick={save} disabled={!canSave} style={{ marginTop: 14, height: 46, borderRadius: 12 }}>
            {saving
              ? <><Loader size={16} className="spin" /> {regenOnly ? '生成中…' : '保存并生成中…'}</>
              : (regenOnly ? '重新生成最终图' : '保存并重新生成最终图')}
          </button>
        </div>
      </div>
    </div>,
    document.body
    )}
    {viewing && <ImageViewer rec={originals} onClose={() => setViewing(false)} />}
  </>);
}
